<template>
  <div class="catalog-page">
    <div v-if="loading" class="details-loading">
      <q-spinner-dots color="primary" size="40px" />
    </div>

    <div v-else-if="error" class="details-error">
      <q-banner rounded class="bg-red-1 text-negative">{{ error }}</q-banner>
      <q-btn class="q-mt-md" label="Tentar novamente" icon="refresh" @click="load" />
    </div>

    <template v-else-if="item">
      <section class="gallery-section">
        <div class="gallery-inner">
          <button class="back-btn" @click="router.back()">
            <q-icon name="arrow_back" size="16px" />
            <span>Voltar</span>
          </button>
          <!-- Mesmo modelo da galeria do frontend: imagem principal + até 4 miniaturas -->
          <div
            class="gallery-grid"
            :class="{ 'gallery-grid--single': thumbnailImages.length === 0 }"
            :aria-label="`Galeria de ${item.title || categoryLabel}`"
          >
            <button class="gallery-main" type="button" @click="openGallery">
              <img v-if="currentImage" :src="currentImage" :alt="item.title || categoryLabel" />
              <span v-else class="image-placeholder"><q-icon :name="icon" size="52px" /></span>
              <span class="gallery-badges">
                <span>{{ categoryLabel }}</span>
                <span class="gallery-badge--light">{{ statusLabel(item.status) }}</span>
              </span>
              <span v-if="images.length > 1" class="gallery-arrows">
                <span role="button" aria-label="Imagem anterior" @click.stop="moveImage(-1)">‹</span>
                <span role="button" aria-label="Próxima imagem" @click.stop="moveImage(1)">›</span>
              </span>
            </button>

            <div
              v-if="thumbnailImages.length"
              class="gallery-thumbnails"
              :class="`gallery-thumbnails--${thumbnailImages.length}`"
            >
              <button
                v-for="(image, index) in thumbnailImages"
                :key="image.id || image.url"
                type="button"
                :class="{ 'is-active': selectedImage === index + 1 }"
                :aria-label="`Ver imagem ${index + 2}`"
                @click="selectImage(index + 1)"
              >
                <img :src="image.url" :alt="`${item.title || categoryLabel} — fotografia ${index + 2}`" />
                <span v-if="index === thumbnailImages.length - 1 && images.length > 5" class="gallery-more">
                  Ver todas ({{ images.length }})
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <main class="info-section">
        <div class="info-inner">
          <div class="details-column">
            <header class="title-block">
              <div class="category-label">{{ categoryLabel }}</div>
              <h1 class="listing-title">{{ item.title }}</h1>
              <div class="location-row"><q-icon name="location_on" size="16px" /><span>{{ item.location || "Localização não indicada" }}</span></div>
              <div class="price-block">
                <div class="price-label">{{ kind === "accommodation" ? "Preço por noite" : "Preço diário" }}</div>
                <div class="price-value">{{ mainPrice }}</div>
              </div>
            </header>

            <div class="section-divider" />

            <section class="info-block">
              <h2 class="block-title">Características</h2>
              <div class="feature-grid">
                <div v-for="feature in features" :key="feature.label" class="feature-card">
                  <div class="feature-icon"><q-icon :name="feature.icon" size="20px" /></div>
                  <div class="feature-value">{{ feature.value }}</div>
                  <div class="feature-label">{{ feature.label }}</div>
                </div>
              </div>
            </section>

            <div class="section-divider" />

            <section v-if="kind === 'accommodation' && item.units?.length" class="info-block">
              <h2 class="block-title">Unidades disponíveis</h2>
              <div class="unit-list">
                <article v-for="unit in item.units" :key="unit.id" class="unit-card">
                  <div class="unit-heading">
                    <strong>{{ unit.name || "Unidade" }}</strong>
                    <span>{{ money(unit.price, unit.currency) }} <small>/ noite</small></span>
                  </div>
                  <div class="unit-specs">
                    <span><q-icon name="groups" /> {{ unit.capacity || 1 }} hóspedes</span>
                    <span><q-icon name="bed" /> {{ unit.bedroom_amount || 0 }} quartos</span>
                    <span><q-icon name="bathtub" /> {{ unit.bathroom_amount || 0 }} casas de banho</span>
                  </div>
                  <p v-if="unit.description" class="unit-description">{{ unit.description }}</p>
                </article>
              </div>
            </section>

            <section v-if="kind === 'accommodation' && item.units?.length" class="info-block amenity-block">
              <h2 class="block-title">Comodidades</h2>
              <div class="amenity-list">
                <span v-for="amenity in item.amenities || []" :key="amenity.id" class="amenity-chip">
                  <q-icon name="check_circle" size="15px" /> {{ amenity.name }}
                </span>
                <span v-if="!item.amenities?.length" class="muted">Sem comodidades indicadas.</span>
              </div>
              <p v-if="item.rules" class="rules-text"><strong>Regras:</strong> {{ item.rules }}</p>
            </section>

            <section v-if="kind === 'rental_vehicle' && item.locations?.length" class="info-block amenity-block">
              <h2 class="block-title">Locais de levantamento</h2>
              <div class="amenity-list">
                <span v-for="location in item.locations" :key="location.id" class="amenity-chip">
                  <q-icon name="place" size="15px" /> {{ location.name || location.location || location }}
                </span>
              </div>
            </section>

            <div class="section-divider" />

            <section class="info-block">
              <h2 class="block-title">Descrição</h2>
              <p class="description">{{ item.description || "Sem descrição disponível." }}</p>
            </section>
          </div>

          <aside class="contact-card">
            <div class="agent-row">
              <div class="agent-avatar">
                <img :src="agentPhoto" alt="Fotografia do agente" @error="useAgentFallback" />
              </div>
              <div class="agent-info">
                <div class="agent-name">{{ item.user?.name || "Agente Golden Key" }}</div>
                <div class="agent-role">{{ kind === "accommodation" ? "Anfitrião" : "Agente rent-a-car" }}</div>
              </div>
            </div>

            <div class="card-divider" />

            <div class="contact-actions">
              <a v-if="whatsappUrl" class="contact-button button-whatsapp" :href="whatsappUrl" target="_blank" rel="noopener noreferrer">
                <q-icon name="chat" size="17px" /><span>WhatsApp</span>
              </a>
              <a v-if="item.user?.phone" class="contact-button button-call" :href="`tel:${item.user.phone}`">
                <q-icon name="phone" size="17px" /><span>Ligar agora</span>
              </a>
              <div v-if="!whatsappUrl && !item.user?.phone" class="muted contact-unavailable">Contactos não disponíveis.</div>
            </div>

            <div class="card-divider" />

            <div class="meta-row"><span class="meta-key">Referência</span><span class="meta-value">{{ item.slug }}</span></div>
            <div class="meta-row"><span class="meta-key">Categoria</span><span class="meta-value">{{ categoryLabel }}</span></div>
            <template v-if="kind === 'rental_vehicle'">
              <div class="meta-row"><span class="meta-key">Marca / modelo</span><span class="meta-value">{{ [item.brand, item.model].filter(Boolean).join(" ") || "—" }}</span></div>
              <div class="meta-row"><span class="meta-key">Ano</span><span class="meta-value">{{ item.year || "—" }}</span></div>
            </template>
            <template v-else>
              <div class="meta-row"><span class="meta-key">Unidades</span><span class="meta-value">{{ item.units?.length || 0 }}</span></div>
              <div class="meta-row"><span class="meta-key">Regras</span><span class="meta-value">{{ item.rules || "—" }}</span></div>
            </template>

            <div class="card-divider" />

            <q-btn v-if="canManage" class="sidebar-button button-edit" icon="edit" label="Editar anúncio" unelevated :disable="actionLoading" @click="edit" />
            <q-btn v-if="canManage" class="sidebar-button button-delete q-mt-sm" icon="delete_outline" label="Remover anúncio" unelevated :loading="deleting" :disable="actionLoading" @click="remove" />
            
            <div class="card-divider" />
            <div class="status-line"><span>Estado do anúncio</span><q-badge :class="`status-badge status-${item.status}`">{{ statusLabel(item.status) }}</q-badge></div>
            <div v-if="item.rejection_reason" class="rejection-note"><q-icon name="info" size="15px" /> {{ item.rejection_reason }}</div>
            <q-btn v-if="isAdmin && item.status === 'pending_review'" class="sidebar-button button-approve q-mt-sm" icon="verified" label="Aprovar e publicar" unelevated :loading="actionLoading" :disable="actionLoading" @click="approve" />
            <q-btn v-if="isAdmin && item.status === 'pending_review'" class="sidebar-button button-reject q-mt-sm" icon="cancel" label="Rejeitar anúncio" unelevated :loading="actionLoading" :disable="actionLoading" @click="reject" />
            <q-btn v-if="canManage && canSubmit" class="sidebar-button button-submit q-mt-sm" icon="publish" label="Enviar para validação" unelevated :loading="actionLoading" :disable="actionLoading" @click="publish" />
            <q-btn v-if="canManage && item.status === 'published'" class="sidebar-button button-deactivate q-mt-sm" icon="toggle_off" label="Desactivar anúncio" unelevated :loading="actionLoading" :disable="actionLoading" @click="deactivate" />
          </aside>
        </div>
      </main>
    </template>

    <q-dialog v-model="galleryOpen" maximized>
      <q-card class="bg-black">
        <q-bar class="bg-transparent">
          <q-space />
          <q-btn v-close-popup flat dense icon="close" color="white" aria-label="Fechar galeria" />
        </q-bar>
        <q-card-section class="flex flex-center gallery-dialog-content">
          <q-carousel
            v-model="selectedImage"
            animated
            navigation
            infinite
            arrows
            control-color="primary"
            class="gallery-carousel"
          >
            <q-carousel-slide
              v-for="(image, index) in images"
              :key="image.id || image.url"
              :name="index"
              :img-src="image.url"
            />
          </q-carousel>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import Swal from "sweetalert2";
import apiClient from "@/config/api";

const props = defineProps({ kind: { type: String, required: true } });
const route = useRoute();
const router = useRouter();
const store = useStore();
const item = ref(null);
const loading = ref(true);
const actionLoading = ref(false);
const deleting = ref(false);
const error = ref("");
const selectedImage = ref(0);
const galleryOpen = ref(false);
const fallbackAgent = "https://amzn-s3-goldenkey.s3.us-east-2.amazonaws.com/properties/icon2.png";
const agentPhoto = ref(fallbackAgent);
const moduleName = computed(() => props.kind === "accommodation" ? "accommodations" : "rentalVehicles");
const isAdmin = computed(() => Number(store.state.auth.user?.role) === 1);
const canManage = computed(() => isAdmin.value || Number(store.state.auth.user?.id) === Number(item.value?.user?.id));
const canSubmit = computed(() => ["draft", "inactive", "rejected", "expired"].includes(item.value?.status));
const images = computed(() => {
  const listingImages = (item.value?.images || []).filter((image) => image?.url);
  const unitImages = props.kind === "accommodation"
    ? (item.value?.units || []).flatMap((unit) => unit.images || [])
    : [];
  const uniqueImages = [...listingImages, ...unitImages].filter((image, index, all) =>
    image?.url && all.findIndex((candidate) => candidate.url === image.url) === index
  );
  return uniqueImages.sort((left, right) => Number(Boolean(right.is_primary)) - Number(Boolean(left.is_primary)));
});
const currentImage = computed(() => images.value[selectedImage.value]?.url || "");
const thumbnailImages = computed(() => images.value.slice(1, 5));
const icon = computed(() => props.kind === "accommodation" ? "hotel" : "directions_car");
const categoryLabel = computed(() => props.kind === "accommodation" ? item.value?.type?.name || "Alojamento" : item.value?.category?.name || "Rent-a-car");
const activeUnits = computed(() => (item.value?.units || []).filter((unit) => unit.active !== false));
const mainPrice = computed(() => {
  if (props.kind === "accommodation") {
    const unit = [...activeUnits.value].sort((a, b) => Number(a.price || 0) - Number(b.price || 0))[0];
    return unit ? `${money(unit.price, unit.currency)} / noite` : "Preço sob consulta";
  }
  const terms = item.value?.terms;
  return terms?.daily_price != null ? `${money(terms.daily_price, terms.currency)} / dia` : "Preço sob consulta";
});
const features = computed(() => props.kind === "accommodation" ? [
  { icon: "meeting_room", value: activeUnits.value.length, label: "Unidades" },
  { icon: "groups", value: Math.max(0, ...activeUnits.value.map((unit) => Number(unit.capacity || 0))), label: "Hóspedes" },
  { icon: "bed", value: Math.max(0, ...activeUnits.value.map((unit) => Number(unit.bedroom_amount || 0))), label: "Quartos" },
  { icon: "bathtub", value: Math.max(0, ...activeUnits.value.map((unit) => Number(unit.bathroom_amount || 0))), label: "Casas de banho" },
] : [
  { icon: "directions_car", value: [item.value?.brand, item.value?.model].filter(Boolean).join(" ") || "—", label: "Viatura" },
  { icon: "event_seat", value: item.value?.seats || "—", label: "Lugares" },
  { icon: "settings", value: transmissionLabel(item.value?.transmission), label: "Transmissão" },
  { icon: "local_gas_station", value: item.value?.fuel || "—", label: "Combustível" },
]);
const whatsappUrl = computed(() => {
  const number = (item.value?.user?.whatsapp || item.value?.user?.phone || "").replace(/\D/g, "");
  return number ? `https://wa.me/${number}` : "";
});

function money(value, currency) {
  return `${Number(value || 0).toLocaleString("pt-MZ")} ${currency || "MZN"}`;
}
function transmissionLabel(value) {
  return value === "automatic" ? "Automática" : value === "manual" ? "Manual" : value || "—";
}
function statusLabel(status) {
  return { published: "Publicado", active: "Activo", pending_review: "Aguarda validação", draft: "Rascunho", rejected: "Requer correcção", inactive: "Inactivo", expired: "Expirado" }[status] || status || "Estado indisponível";
}
function useAgentFallback() {
  agentPhoto.value = fallbackAgent;
}
function moveImage(direction) {
  selectedImage.value = (selectedImage.value + direction + images.value.length) % images.value.length;
}
function selectImage(index) {
  selectedImage.value = index;
}
function openGallery() {
  if (images.value.length) galleryOpen.value = true;
}
async function load() {
  loading.value = true;
  error.value = "";
  try {
    item.value = await store.dispatch(`${moduleName.value}/show`, route.params.slug);
    selectedImage.value = 0;
    agentPhoto.value = item.value.user?.photo || fallbackAgent;
  } catch (requestError) {
    item.value = null;
    error.value = requestError.response?.data?.message || "Não foi possível carregar os detalhes deste anúncio.";
  } finally {
    loading.value = false;
  }
}
function edit() {
  router.push(`${props.kind === "accommodation" ? "/alojamentos" : "/rent-a-car"}/editar/${item.value.slug}`);
}
async function remove() {
  const result = await Swal.fire({
    title: "Remover anúncio?",
    text: "Esta acção é permanente e não pode ser revertida.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#dc2626",
    cancelButtonColor: "#64748b",
    confirmButtonText: "Sim, remover",
    cancelButtonText: "Cancelar",
  });
  if (!result.isConfirmed) return;

  actionLoading.value = true;
  deleting.value = true;
  try {
    await store.dispatch(`${moduleName.value}/destroy`, item.value.slug);
    await Swal.fire({
      title: "Anúncio removido",
      text: "O anúncio foi removido com sucesso.",
      icon: "success",
      timer: 2000,
      showConfirmButton: false,
    });
    await router.push(props.kind === "accommodation" ? "/alojamentos" : "/rent-a-car");
  } catch (requestError) {
    Swal.fire(
      "Não foi possível remover o anúncio",
      requestError.response?.data?.message || "Tente novamente.",
      "error",
    );
  } finally {
    deleting.value = false;
    actionLoading.value = false;
  }
}
async function publish() {
  actionLoading.value = true;
  try {
    item.value = await store.dispatch(`${moduleName.value}/publish`, item.value.id);
    Swal.fire({ title: "Enviado para validação", text: "O anúncio aguarda análise do administrador.", icon: "success", timer: 2200, showConfirmButton: false });
  } catch (requestError) {
    Swal.fire("Não foi possível publicar", requestError.response?.data?.message || "Verifique os dados obrigatórios.", "error");
  } finally {
    actionLoading.value = false;
  }
}
async function deactivate() {
  const result = await Swal.fire({ title: "Desactivar anúncio?", text: "O anúncio ficará oculto na plataforma.", icon: "warning", showCancelButton: true, confirmButtonText: "Desactivar", cancelButtonText: "Cancelar" });
  if (!result.isConfirmed) return;
  actionLoading.value = true;
  try {
    item.value = await store.dispatch(`${moduleName.value}/deactivate`, item.value.id);
    Swal.fire({ title: "Anúncio desactivado", icon: "success", timer: 1800, showConfirmButton: false });
  } catch (requestError) {
    Swal.fire("Não foi possível desactivar", requestError.response?.data?.message || "Tente novamente.", "error");
  } finally {
    actionLoading.value = false;
  }
}
async function approve() {
  actionLoading.value = true;
  try {
    const type = props.kind === "accommodation" ? "accommodation" : "rental_vehicle";
    const response = await apiClient.post(`/moderation/${type}/${item.value.id}/approve`);
    item.value = response.data.data;
    await load();
    Swal.fire({ title: "Anúncio aprovado", text: "O anúncio foi publicado.", icon: "success", timer: 2000, showConfirmButton: false });
  } catch (requestError) {
    Swal.fire("Não foi possível aprovar", requestError.response?.data?.message || "Tente novamente.", "error");
  } finally {
    actionLoading.value = false;
  }
}
async function reject() {
  const result = await Swal.fire({
    title: "Rejeitar anúncio",
    input: "textarea",
    inputLabel: "Indique o que o agente deve corrigir",
    inputPlaceholder: "Motivo da rejeição...",
    inputValidator: (value) => value?.trim() ? undefined : "Indique o motivo da rejeição.",
    showCancelButton: true,
    confirmButtonText: "Rejeitar",
    cancelButtonText: "Cancelar",
  });
  if (!result.isConfirmed) return;
  actionLoading.value = true;
  try {
    const type = props.kind === "accommodation" ? "accommodation" : "rental_vehicle";
    await apiClient.post(`/moderation/${type}/${item.value.id}/reject`, { reason: result.value.trim() });
    await load();
    Swal.fire({ title: "Anúncio devolvido para correcção", icon: "success", timer: 2000, showConfirmButton: false });
  } catch (requestError) {
    Swal.fire("Não foi possível rejeitar", requestError.response?.data?.message || "Tente novamente.", "error");
  } finally {
    actionLoading.value = false;
  }
}
onMounted(load);
</script>

<style scoped>
.catalog-page { min-height: 100vh; background: #f4f6fb; }
.details-loading, .details-error { max-width: 1100px; min-height: 55vh; margin: 0 auto; padding: 40px 20px; }
.gallery-section { padding: 24px 0 32px; border-bottom: 1px solid #e8ecf4; background: #fff; }
.gallery-inner, .info-inner { max-width: 1400px; margin: 0 auto; padding: 0 20px; }
.back-btn { display: inline-flex; align-items: center; gap: 6px; margin: 0 0 20px; padding: 8px 16px; border: 0; border-radius: 10px; background: #f1f5f9; color: #475569; font-size: 13px; font-weight: 600; cursor: pointer; transition: background .18s, color .18s; }
.back-btn:hover { background: #0d1b2a; color: #fff; }
.gallery-grid { display: grid; grid-template-columns: minmax(0, 2fr) minmax(300px, 1fr); grid-template-rows: 502px; gap: 12px; }
.gallery-grid--single { grid-template-columns: 1fr; }
.gallery-main, .gallery-thumbnails button { position: relative; min-width: 0; padding: 0; overflow: hidden; border: 0; border-radius: 12px; background: #e8ecf4; cursor: pointer; }
.gallery-main > img, .gallery-thumbnails img { width: 100%; height: 100%; display: block; object-fit: cover; transition: transform .35s ease; }
.gallery-main:hover > img, .gallery-thumbnails button:hover img { transform: scale(1.025); }
.image-placeholder { display: grid; width: 100%; height: 100%; place-items: center; color: #94a3b8; }
.gallery-thumbnails { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); grid-template-rows: repeat(2, minmax(0, 1fr)); gap: 12px; min-width: 0; }
.gallery-thumbnails--1 button { grid-column: 1 / -1; grid-row: 1 / -1; }
.gallery-thumbnails--2 button { grid-column: 1 / -1; }
.gallery-thumbnails--3 button:first-child { grid-row: 1 / -1; }
.gallery-thumbnails button.is-active { box-shadow: inset 0 0 0 3px #d4af37; }
.gallery-badges { position: absolute; left: 14px; bottom: 14px; display: flex; flex-wrap: wrap; gap: 8px; }
.gallery-badges span { padding: 6px 11px; border-radius: 999px; color: #fff; background: #0d1b2a; font-size: 11px; line-height: 14px; font-weight: 600; }
.gallery-badges .gallery-badge--light { color: #0d1b2a; background: #fff; }
.gallery-arrows { position: absolute; inset: 0 14px; display: flex; align-items: center; justify-content: space-between; pointer-events: none; }
.gallery-arrows span { width: 38px; height: 38px; display: grid; place-items: center; border-radius: 50%; color: #0d1b2a; background: #fff; box-shadow: 0 2px 8px rgba(13,27,42,.18); font-size: 27px; line-height: 1; pointer-events: auto; }
.gallery-more { position: absolute; right: 10px; bottom: 10px; padding: 7px 10px; border-radius: 6px; color: #fff; background: rgba(13,27,42,.8); font-size: 11px; font-weight: 600; }
@media (max-width: 1100px) { .gallery-grid { grid-template-rows: 420px; } }
@media (max-width: 820px) {
  .gallery-grid { grid-template-columns: 1fr; grid-template-rows: minmax(300px, 58vw) 150px; }
  .gallery-grid--single { grid-template-rows: minmax(300px, 58vw); }
  .gallery-thumbnails { grid-template-columns: repeat(4, minmax(0, 1fr)); grid-template-rows: 1fr; }
  .gallery-thumbnails--1 { grid-template-columns: 1fr; }
  .gallery-thumbnails--2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .gallery-thumbnails--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .gallery-thumbnails--1 button, .gallery-thumbnails--2 button, .gallery-thumbnails--3 button:first-child { grid-column: auto; grid-row: auto; }
}
@media (max-width: 600px) {
  .gallery-grid { grid-template-rows: 64vw 82px; gap: 8px; }
  .gallery-grid--single { grid-template-rows: 64vw; }
  .gallery-thumbnails { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
  .gallery-thumbnails--1 { grid-template-columns: 1fr; }
  .gallery-thumbnails--2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .gallery-thumbnails button:nth-child(n+4) { display: none; }
  .gallery-badges { left: 10px; bottom: 10px; }
  .gallery-arrows { display: none; }
}
@media (prefers-reduced-motion: reduce) { .gallery-main > img, .gallery-thumbnails img { transition: none; } }
.info-section { padding: 36px 0 72px; }
.info-inner { display: grid; grid-template-columns: minmax(0, 2fr) minmax(280px, 1fr); align-items: start; gap: 32px; }
.title-block { margin-bottom: 24px; }
.category-label { margin-bottom: 7px; color: #c69e26; font-size: 10px; font-weight: 800; letter-spacing: 1px; text-transform: uppercase; }
.listing-title { margin: 0 0 9px; color: #0d1b2a; font-size: 27px; font-weight: 800; line-height: 1.25; }
.location-row { display: flex; align-items: center; gap: 4px; margin-bottom: 19px; color: #64748b; font-size: 13px; }
.location-row .q-icon { color: #94a3b8; }
.price-label { margin-bottom: 4px; color: #94a3b8; font-size: 10px; font-weight: 800; letter-spacing: .8px; text-transform: uppercase; }
.price-value { color: #0d1b2a; font-size: 28px; font-weight: 800; letter-spacing: -.7px; line-height: 1.2; }
.section-divider { height: 1px; margin: 28px 0; background: #e8ecf4; }
.block-title { margin: 0 0 15px; color: #0d1b2a; font-size: 14px; font-weight: 800; }
.feature-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
.feature-card { min-width: 0; padding: 18px 12px; border: 1px solid #e8ecf4; border-radius: 14px; background: #fff; text-align: center; transition: transform .25s ease, box-shadow .25s ease; }
.feature-card:hover { transform: translateY(-3px); box-shadow: 0 8px 20px rgba(13,27,42,.08); }
.feature-icon { display: grid; width: 40px; height: 40px; margin: 0 auto 10px; place-items: center; border-radius: 12px; background: #f1f5f9; color: #0d1b2a; }
.feature-value { overflow-wrap: anywhere; color: #0d1b2a; font-size: 22px; font-weight: 800; line-height: 1; }
.feature-label { margin-top: 4px; color: #94a3b8; font-size: 10px; font-weight: 700; letter-spacing: .5px; text-transform: uppercase; }
.description { margin: 0; color: #475569; font-size: 14px; line-height: 1.85; white-space: pre-line; }
.unit-list { display: grid; gap: 10px; }
.unit-card { padding: 14px; border: 1px solid #e8ecf4; border-radius: 12px; background: #fff; }
.unit-heading { display: flex; align-items: center; justify-content: space-between; gap: 10px; color: #0d1b2a; }
.unit-heading span { color: #0d1b2a; font-weight: 800; }
.unit-heading small { color: #94a3b8; font-size: 10px; font-weight: 600; }
.unit-specs { display: flex; flex-wrap: wrap; gap: 10px 16px; margin-top: 11px; color: #64748b; font-size: 11px; }
.unit-specs span { display: inline-flex; align-items: center; gap: 4px; }
.unit-description, .rules-text { margin: 10px 0 0; color: #64748b; font-size: 12px; line-height: 1.65; }
.amenity-block { margin-top: 22px; }
.amenity-list { display: flex; flex-wrap: wrap; gap: 8px; }
.amenity-chip { display: inline-flex; align-items: center; gap: 5px; padding: 6px 9px; border-radius: 18px; background: #fff; color: #475569; font-size: 11px; }
.amenity-chip .q-icon { color: #16a34a; }
.muted { color: #94a3b8; font-size: 12px; }
.contact-card { position: sticky; top: 24px; padding: 28px; border: 1px solid #e8ecf4; border-radius: 18px; background: #fff; box-shadow: 0 4px 20px rgba(13,27,42,.06); }
.agent-row { display: flex; align-items: center; gap: 16px; }
.agent-avatar { width: 68px; height: 68px; flex: 0 0 68px; overflow: hidden; border: 3px solid #e8ecf4; border-radius: 50%; background: #fff; box-shadow: 0 2px 8px rgba(13,27,42,.08); }
.agent-avatar img { width: 100%; height: 100%; object-fit: cover; }
.agent-name { color: #0d1b2a; font-size: 18px; font-weight: 700; }
.agent-role { margin-top: 3px; color: #94a3b8; font-size: 13px; }
.card-divider { height: 1px; margin: 22px 0; background: #f1f5f9; }
.contact-actions { display: flex; flex-direction: column; gap: 10px; }
.contact-button { display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%; min-height: 50px; padding: 12px 16px; border-radius: 13px; font-size: 15px; font-weight: 700; text-decoration: none; }
.button-whatsapp { background: #f0fdf4; color: #15803d; }
.button-call { background: #f1f5f9; color: #0d1b2a; }
.contact-unavailable { padding: 3px 0; }
.meta-row { display: flex; justify-content: space-between; gap: 12px; padding: 10px 0; border-bottom: 1px solid #f1f5f9; font-size: 13px; }
.meta-key { color: #94a3b8; }
.meta-value { color: #0d1b2a; font-weight: 700; text-align: right; overflow-wrap: anywhere; }
.sidebar-button { width: 100%; min-height: 50px; border-radius: 12px; font-size: 14px; font-weight: 800; letter-spacing: .15px; transition: background .18s, color .18s, box-shadow .18s; }
.sidebar-button:not(.q-btn--disabled):hover { box-shadow: 0 4px 12px rgba(13,27,42,.12); }
.button-edit { border: 1px solid #0d1b2a; background: #0d1b2a; color: #d4af37; }
.button-edit:not(.q-btn--disabled):hover { background: #d4af37; color: #0d1b2a; }
.button-delete { border: 1px solid rgba(220,38,38,.24); background: rgba(220,38,38,.06); color: #dc2626; }
.button-delete:not(.q-btn--disabled):hover { background: rgba(220,38,38,.12); }
.button-submit, .button-approve { border: 1px solid #15803d; background: #15803d; color: #fff; }
.button-reject, .button-deactivate { border: 1px solid #dc2626; background: #dc2626; color: #fff; }
.button-submit:not(.q-btn--disabled):hover, .button-approve:not(.q-btn--disabled):hover { background: #166534; }
.button-reject:not(.q-btn--disabled):hover, .button-deactivate:not(.q-btn--disabled):hover { background: #b91c1c; }
.status-line { display: flex; align-items: center; justify-content: space-between; gap: 8px; color: #64748b; font-size: 13px; }
.status-badge { padding: 5px 9px; border-radius: 10px; color: #fff; font-size: 11px; font-weight: 800; }
.status-published, .status-active { background: #15803d; }
.status-pending_review { background: #d97706; }
.status-draft, .status-inactive, .status-expired { background: #64748b; }
.status-rejected { background: #dc2626; }
.rejection-note { display: flex; align-items: flex-start; gap: 5px; margin-top: 10px; padding: 8px; border-radius: 8px; background: #fef2f2; color: #b91c1c; font-size: 10px; line-height: 1.5; }
.gallery-dialog-content { height: calc(100vh - 50px); }
.gallery-carousel { width: 90vw; max-width: 1200px; height: 80vh; }
:deep(.gallery-carousel .q-carousel__slide) { background-size: contain; background-repeat: no-repeat; background-position: center; }
@media (max-width: 1023px) { .contact-card { position: relative; top: 0; } }
@media (max-width: 650px) { .gallery-section { padding: 16px 0 22px; } .gallery-inner, .info-inner { padding: 0 12px; } .info-section { padding: 26px 0 45px; } .info-inner { grid-template-columns: 1fr; gap: 24px; } .contact-card { position: relative; top: 0; padding: 22px; } .agent-avatar { width: 60px; height: 60px; flex-basis: 60px; } .feature-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
</style>
